import { showToast } from '../../../engine/toast';
import {
  adminCreateSocial, adminDeleteSocial, adminUpdateSocial,
  type CmsSocial,
} from '../admin-api';
import { escapeHtml, BTN_CLS, SMALL_BTN_CLS } from '../admin-ui';
import { SOCIAL_NETWORKS, findSocialNetwork, safeSocialUrl } from '../../../engine/social-networks';
import type { PaneCtx, SocialDraftRow } from './types';

/**
 * The CMS "Social" tab: a small builder for the footer's social links.
 *
 * The working copy lives in `state.cms.socialDraft` and is saved as a whole
 * (create / update / delete diffed against what the server holds). Inputs are
 * plain DOM — the screen only repaints on add/remove/save, so before any
 * repaint the draft is re-read from the form (`readDraft`), otherwise typing
 * would be lost.
 */

let keySeq = 0;
const newKey = (): string => `n${++keySeq}`;

export function draftFromServer(rows: CmsSocial[]): SocialDraftRow[] {
  return [...rows]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((r) => ({ key: newKey(), id: r.id, platform: r.platform, url: r.url, active: r.active }));
}

/** INPUT_CLS forces `w-full`, which fights any width set beside it — so the row uses its own control look. */
const CONTROL_CLS =
  'min-h-11 px-2 bg-surface-2 border border-border rounded text-text text-sm focus:border-gold outline-none';

function iconPreview(platform: string): string {
  const net = findSocialNetwork(platform);
  if (!net) return '';
  return `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor"><path d="${net.path}"/></svg>`;
}

function rowHtml(r: SocialDraftRow, busy: boolean): string {
  const net = findSocialNetwork(r.platform);
  return `
    <div data-social-row data-key="${r.key}"
      class="flex flex-wrap items-center gap-x-2 gap-y-2 p-2 bg-surface-2 border border-border rounded transition-shadow">
      <div class="flex items-center gap-2 shrink-0">
        <button type="button" data-drag-handle aria-label="Drag to reorder (or use the arrow keys)"
          class="drag-ribs shrink-0 w-6 min-h-11 rounded touch-none cursor-grab"></button>
        <label class="flex items-center gap-1.5 min-h-11 px-1" title="Show in the footer">
          <input name="active" type="checkbox" ${r.active ? 'checked' : ''} class="w-4 h-4 accent-gold" aria-label="Active" />
          <span class="text-[10px] text-text-dim uppercase tracking-wide">Active</span>
        </label>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <span data-social-preview class="w-6 flex justify-center" style="color:${net?.color ?? 'inherit'}">${iconPreview(r.platform)}</span>
        <select name="platform" aria-label="Social network" class="${CONTROL_CLS} w-36">
          ${SOCIAL_NETWORKS.map(
            (n) => `<option value="${n.id}" ${n.id === r.platform ? 'selected' : ''}>${escapeHtml(n.label)}</option>`,
          ).join('')}
        </select>
      </div>
      <input name="url" type="text" inputmode="url" value="${escapeHtml(r.url)}" placeholder="https://…"
        aria-label="Profile URL" autocomplete="off" autocapitalize="off" spellcheck="false"
        class="${CONTROL_CLS} flex-[1_1_14rem] min-w-0" />
      <button type="button" data-action="social-remove" data-key="${r.key}" aria-label="Remove" ${busy ? 'disabled' : ''}
        class="${SMALL_BTN_CLS} shrink-0 ml-auto">✕</button>
    </div>`;
}

export function socialHtml(ctx: PaneCtx): string {
  const { socialDraft: draft } = ctx.state.cms;
  const busy = ctx.state.busy;
  return `
    <form data-form="social" id="social-form" novalidate class="flex flex-col gap-3">
      <div data-social-list class="flex flex-col gap-2">
        ${
          draft.length === 0
            ? '<p data-social-empty class="glass-panel px-3 py-4 text-xs text-text-dim text-center">No social networks added yet.</p>'
            : draft.map((r) => rowHtml(r, busy)).join('')
        }
      </div>
      <div class="flex items-center justify-between gap-2">
        <button type="button" data-action="social-add" ${busy ? 'disabled' : ''} class="${SMALL_BTN_CLS}">+ Add social network</button>
        <button type="submit" ${busy ? 'disabled' : ''} class="${BTN_CLS}">Save</button>
      </div>
      <p class="text-[10px] text-text-dim leading-relaxed">
        Drag the ribbed handle on the left of a row (or focus it and use the up/down arrow keys) to change the order
        the icons appear in the site footer. Inactive rows are kept but not shown. Changes go live on Save.
      </p>
    </form>`;
}

/** Re-reads the draft from the form, in DOM order, keeping row identity. */
function readDraft(ctx: PaneCtx): void {
  const list = document.querySelector('[data-social-list]');
  if (!list) return;
  const byKey = new Map(ctx.state.cms.socialDraft.map((r) => [r.key, r]));
  const next: SocialDraftRow[] = [];
  list.querySelectorAll<HTMLElement>('[data-social-row]').forEach((row) => {
    const prev = byKey.get(row.dataset.key ?? '');
    if (!prev) return;
    next.push({
      ...prev,
      platform: (row.querySelector('[name="platform"]') as HTMLSelectElement).value,
      url: (row.querySelector('[name="url"]') as HTMLInputElement).value.trim(),
      active: (row.querySelector('[name="active"]') as HTMLInputElement).checked,
    });
  });
  ctx.state.cms.socialDraft = next;
}

function sameAsServer(draft: SocialDraftRow[], server: CmsSocial[]): boolean {
  const sorted = [...server].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    draft.length === sorted.length &&
    draft.every((d, i) => d.id === sorted[i].id && d.platform === sorted[i].platform && d.url === sorted[i].url && d.active === sorted[i].active)
  );
}

async function save(ctx: PaneCtx): Promise<void> {
  readDraft(ctx);
  const draft = ctx.state.cms.socialDraft;
  const bad = draft.findIndex((r) => safeSocialUrl(r.url) === null);
  if (bad >= 0) {
    showToast(`Row ${bad + 1}: enter a full http(s) link, e.g. https://discord.gg/yourserver.`, 'error');
    ctx.repaint();
    return;
  }
  const server = ctx.state.cms.social;
  if (sameAsServer(draft, server)) {
    showToast('Nothing to save.', 'info');
    return;
  }
  await ctx.withBusy(async () => {
    const keep = new Set(draft.map((r) => r.id).filter(Boolean));
    for (const s of server) if (!keep.has(s.id)) await adminDeleteSocial(s.id);
    for (let i = 0; i < draft.length; i++) {
      const r = draft[i];
      const body = { platform: r.platform, icon: r.platform, url: safeSocialUrl(r.url) as string, active: r.active, sortOrder: i };
      const prev = server.find((s) => s.id === r.id);
      if (!r.id) await adminCreateSocial(body);
      else if (!prev || prev.platform !== r.platform || prev.url !== body.url || prev.active !== r.active || prev.sortOrder !== i) {
        await adminUpdateSocial(r.id, body);
      }
    }
    showToast('Social networks saved.', 'success');
    await ctx.reload('cms');
  }, 'Could not save the social networks.');
}

export function socialAction(action: string, el: HTMLElement, ctx: PaneCtx): boolean {
  if (action === 'social-add') {
    readDraft(ctx);
    const used = new Set(ctx.state.cms.socialDraft.map((r) => r.platform));
    const first = SOCIAL_NETWORKS.find((n) => !used.has(n.id)) ?? SOCIAL_NETWORKS[0];
    ctx.state.cms.socialDraft.push({ key: newKey(), platform: first.id, url: '', active: true });
    ctx.repaint();
    return true;
  }
  if (action === 'social-remove') {
    readDraft(ctx);
    ctx.state.cms.socialDraft = ctx.state.cms.socialDraft.filter((r) => r.key !== el.dataset.key);
    ctx.repaint();
    return true;
  }
  return false;
}

export function socialSubmit(_form: HTMLFormElement, ctx: PaneCtx): boolean {
  void save(ctx);
  return true;
}

/** Network dropdown changed: update that row's icon + colour in place (no repaint, focus stays). */
export function socialChange(el: HTMLElement): boolean {
  if (!(el instanceof HTMLSelectElement) || el.name !== 'platform') return false;
  const net = findSocialNetwork(el.value);
  const preview = el.closest('[data-social-row]')?.querySelector<HTMLElement>('[data-social-preview]');
  if (preview) {
    preview.innerHTML = iconPreview(el.value);
    preview.style.color = net?.color ?? 'inherit';
  }
  return true;
}

/**
 * Drag-to-reorder. Pointer events (mouse, touch and pen alike) on the ribbed
 * handle; the row is moved live in the DOM under the pointer and the draft is
 * re-read from DOM order on release.
 */
export function socialPointerDown(e: PointerEvent, ctx: PaneCtx): boolean {
  const handle = (e.target as HTMLElement).closest<HTMLElement>('[data-drag-handle]');
  if (!handle || (e.pointerType === 'mouse' && e.button !== 0)) return false;
  const row = handle.closest<HTMLElement>('[data-social-row]');
  const list = row?.parentElement;
  if (!row || !list) return false;
  e.preventDefault();
  handle.setPointerCapture?.(e.pointerId);
  row.classList.add('shadow-lg', 'border-gold', 'z-10', 'relative');

  const onMove = (ev: PointerEvent): void => {
    const siblings = [...list.querySelectorAll<HTMLElement>('[data-social-row]')].filter((r) => r !== row);
    const after = siblings.find((r) => {
      const box = r.getBoundingClientRect();
      return ev.clientY < box.top + box.height / 2;
    });
    if (after) {
      if (row.nextElementSibling !== after) list.insertBefore(row, after);
    } else if (list.lastElementChild !== row) {
      list.appendChild(row);
    }
  };
  const end = (): void => {
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', end);
    handle.removeEventListener('pointercancel', end);
    row.classList.remove('shadow-lg', 'border-gold', 'z-10', 'relative');
    readDraft(ctx);
  };
  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);
  return true;
}

/** Keyboard alternative to dragging: ArrowUp / ArrowDown on the focused handle. */
export function socialKeyDown(e: KeyboardEvent, ctx: PaneCtx): boolean {
  const handle = (e.target as HTMLElement).closest<HTMLElement>('[data-drag-handle]');
  if (!handle || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return false;
  const row = handle.closest<HTMLElement>('[data-social-row]');
  if (!row) return false;
  e.preventDefault();
  const sibling = e.key === 'ArrowUp' ? row.previousElementSibling : row.nextElementSibling;
  if (!sibling) return true;
  if (e.key === 'ArrowUp') row.parentElement?.insertBefore(row, sibling);
  else row.parentElement?.insertBefore(sibling, row);
  handle.focus();
  readDraft(ctx);
  return true;
}
